import { useEffect, useState } from "react";
import DashboardLayout, { StatCard, EmptyState } from "./DashboardLayout";
import { LayoutDashboard, GraduationCap, Users, ShieldCheck, Brain, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const NAV = [
    { key: "overview", label: "School Dashboard", icon: LayoutDashboard },
    { key: "classes", label: "Classes 1–12", icon: GraduationCap },
];

function Bar({ label, value, color = "primary" }) {
    return (
        <div>
            <div className="flex justify-between text-xs text-foreground/60 mb-1"><span>{label}</span><span className="font-medium">{value}%</span></div>
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full bg-${color}`} style={{ width: `${value}%` }} /></div>
        </div>
    );
}

export default function SchoolDashboard() {
    const [tab, setTab] = useState("overview");
    const [data, setData] = useState(null);
    useEffect(() => {
        import("@/lib/api").then(({ default: api }) => api.get("/school/dashboard").then((r) => setData(r.data)).catch(() => setData({})));
    }, []);
    if (!data) return <DashboardLayout nav={NAV} active={tab} onNav={setTab} title="School Portal"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></DashboardLayout>;

    const s = data.stats || {};
    return (
        <DashboardLayout nav={NAV} active={tab} onNav={setTab} title={data.school ? data.school.name : "School Portal"}>
            {!data.school ? <EmptyState icon={GraduationCap} title="No school linked" desc="Your institution account is being set up." /> : tab === "overview" ? (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard icon={Users} label="Students" value={s.total_students} />
                        <StatCard icon={GraduationCap} label="Classes" value={s.classes} color="accent" />
                        <StatCard icon={ShieldCheck} label="Safety Training" value={`${s.safety_training}%`} color="success" />
                        <StatCard icon={Brain} label="AI Readiness" value={`${s.ai_readiness}%`} />
                    </div>
                    <Card className="p-6">
                        <h3 className="font-heading font-bold mb-5">Institution readiness</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                            <Bar label="Student participation" value={s.participation} />
                            <Bar label="Digital readiness" value={s.digital_readiness} />
                            <Bar label="AI readiness" value={s.ai_readiness} color="accent" />
                            <Bar label="Teacher readiness" value={s.teacher_readiness} color="success" />
                            <Bar label="Project completion" value={s.project_completion} />
                            <Bar label="Digital safety training" value={s.safety_training} color="success" />
                        </div>
                    </Card>
                </div>
            ) : (
                <Card className="p-0 overflow-hidden overflow-x-auto">
                    <Table>
                        <TableHeader><TableRow><TableHead>Grade band</TableHead><TableHead>Curriculum</TableHead><TableHead>Students</TableHead><TableHead>Digital</TableHead><TableHead>AI</TableHead><TableHead>Safety</TableHead></TableRow></TableHeader>
                        <TableBody>
                            {(data.classes || []).map((c) => (
                                <TableRow key={c.id} data-testid={`class-${c.grade_order}`}>
                                    <TableCell className="font-medium">{c.grade}</TableCell>
                                    <TableCell><Badge variant="secondary" className="rounded-full">{c.curriculum}</Badge></TableCell>
                                    <TableCell>{c.students}</TableCell>
                                    <TableCell>{c.digital_readiness}%</TableCell>
                                    <TableCell>{c.ai_readiness}%</TableCell>
                                    <TableCell>{c.safety_training}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </DashboardLayout>
    );
}
